// Graphe de similarité projets–projets — la partie calculable, sans dessin.
//
// Portage de Similarite.aretes et du réglage du curseur de poc/src/app.js. Le
// graphe ne trace un trait que si le score dépasse le seuil : les grappes sont les
// familles de projets, les nœuds détachés les projets sans équivalent
// (poc/docs/vues/specification.md § 2.3–2.4).

import type { Similarites } from "./similarite";
import type { ClasseBioagresseur } from "./types";

export interface Arete {
  /** Indices des deux projets, `a < b`. */
  a: number;
  b: number;
  score: number;
}

/** Toutes les paires, chacune une seule fois, dans l'ordre (a, b) croissant. */
export function paires(similarites: Similarites): Arete[] {
  const resultat: Arete[] = [];
  similarites.voisins.forEach((voisins, a) => {
    for (const v of voisins) if (v.autre > a) resultat.push({ a, b: v.autre, score: v.score });
  });
  return resultat.sort((x, y) => x.a - y.a || x.b - y.b);
}

/**
 * Ce qui tient le placement : les plus proches voisins (par défaut, stable quand
 * le seuil bouge) ou les seuls liens tracés (le placement d'origine du POC).
 */
export type Placement = "voisins" | "liens";
export const PLACEMENT_PAR_DEFAUT: Placement = "voisins";
export const PLACEMENTS: { id: Placement; libelle: string; detail: string }[] = [
  {
    id: "voisins",
    libelle: "Plus proches voisins",
    detail: "Chaque projet près de ses trois plus ressemblants ; le seuil ne déplace rien.",
  },
  {
    id: "liens",
    libelle: "Liens tracés",
    detail:
      "Seuls les liens au-dessus du seuil rapprochent ; le graphe se redispose avec le seuil.",
  },
];
export const estPlacement = (v: string | null): v is Placement =>
  PLACEMENTS.some((p) => p.id === v);

/** Voisins par projet qui tiennent le placement du graphe (ADR-0018). */
export const VOISINS_DE_PLACEMENT = 3;

/**
 * Les ressorts du placement : chaque projet tenu à ses `k` plus proches voisins,
 * que le lien soit tracé ou non. Le placement ne dépend donc pas du seuil — le
 * seuil ne décide que des traits — et un projet sans trait reste près de ceux
 * qui lui ressemblent le plus. Paires sans doublon, (a, b) croissant ; les
 * scores nuls ne tiennent rien.
 */
export function aretesDePlacement(
  similarites: Similarites,
  k: number = VOISINS_DE_PLACEMENT,
): Arete[] {
  const vues = new Set<string>();
  const resultat: Arete[] = [];
  similarites.voisins.forEach((voisins, i) => {
    for (const v of voisins.slice(0, k)) {
      if (v.score <= 0) continue;
      const a = Math.min(i, v.autre);
      const b = Math.max(i, v.autre);
      const cle = `${a}-${b}`;
      if (vues.has(cle)) continue;
      vues.add(cle);
      resultat.push({ a, b, score: v.score });
    }
  });
  return resultat.sort((x, y) => x.a - y.a || x.b - y.b);
}

/** Les paires au-dessus du seuil, de la plus proche à la plus lointaine. */
export function aretes(similarites: Similarites, seuil: number): Arete[] {
  return paires(similarites)
    .filter((p) => p.score >= seuil)
    .sort((x, y) => y.score - x.score);
}

/**
 * Seuil qui donne environ `parProjet` arêtes par projet, arrondi au centième.
 *
 * ADR-0005 R2 du POC : ni la plage ni le seuil par défaut ne sont écrits en dur.
 * La densité est la grandeur qui décide de la lisibilité ; on remonte à un seuil
 * par la distribution des scores.
 */
export function seuilPourDensite(similarites: Similarites, parProjet: number): number {
  const n = similarites.voisins.length;
  const vises = Math.round(parProjet * n);
  const tries = paires(similarites)
    .map((p) => p.score)
    .sort((a, b) => b - a);
  const dernier = tries[tries.length - 1];
  if (dernier === undefined) return 0.5;
  if (vises >= tries.length) return Math.floor(dernier * 100) / 100;
  return Math.round((tries[Math.max(0, vises - 1)] ?? dernier) * 100) / 100;
}

export interface PlageSeuil {
  min: number;
  max: number;
  defaut: number;
}

/** Bornes et valeur par défaut du curseur, exprimées en densité (arêtes par projet). */
export function plageDeSeuil(similarites: Similarites): PlageSeuil {
  const min = seuilPourDensite(similarites, 4.0); // au-delà, c'est une pelote
  let max = seuilPourDensite(similarites, 0.1); // en deçà, il ne reste presque rien
  if (max <= min) max = min + 0.1;
  const defaut = Math.min(max, Math.max(min, seuilPourDensite(similarites, 1.5)));
  return { min, max, defaut };
}

export interface Isole {
  indice: number;
  /** Score du meilleur voisin, sous le seuil par définition ; null sans voisin. */
  meilleur: number | null;
}

/** Projets sans arête à ce seuil, du plus singulier au moins singulier. */
export function projetsIsoles(similarites: Similarites, liens: readonly Arete[]): Isole[] {
  const lies = new Set<number>();
  for (const e of liens) {
    lies.add(e.a);
    lies.add(e.b);
  }
  return similarites.voisins
    .map((v, indice) => ({ indice, meilleur: v[0]?.score ?? null }))
    .filter((x) => !lies.has(x.indice))
    .sort((x, y) => (x.meilleur ?? 0) - (y.meilleur ?? 0));
}

/**
 * Classes qui reçoivent une teinte. Au-delà de trois, plus aucune (ADR-0005 R4 du
 * POC) : la palette n'est validée qu'à trois teintes en comparaison « toutes
 * paires », ce qu'est un nuage de nœuds.
 */
export function classesColorees(classes: readonly ClasseBioagresseur[]): ClasseBioagresseur[] {
  return classes.length <= 3 ? [...classes] : [];
}

/**
 * Emplacement de palette (1 à 3) d'une classe, ou null pour la teinte neutre.
 * Attribué par position dans le référentiel, jamais par effectif : un filtre ne
 * repeint pas les survivants.
 */
export function emplacementCouleur(
  classe: string,
  colorees: readonly ClasseBioagresseur[],
): number | null {
  const i = colorees.findIndex((c) => c.id === classe);
  return i === -1 ? null : i + 1;
}

/**
 * Composantes connexes du graphe à ce seuil, chacune triée par indice. Les
 * familles sont celles de plus d'un projet ; les autres sont les projets sans
 * équivalent (portage de Similarite.grappes du POC).
 */
export function grappes(nombre: number, liens: readonly Arete[]): number[][] {
  const parent = Array.from({ length: nombre }, (_, i) => i);
  const trouver = (x: number): number => {
    let r = x;
    while (parent[r] !== r) {
      const p = parent[r] ?? r;
      parent[r] = parent[p] ?? p;
      r = p;
    }
    return r;
  };
  for (const e of liens) {
    const ra = trouver(e.a);
    const rb = trouver(e.b);
    if (ra !== rb) parent[ra] = rb;
  }
  const groupes = new Map<number, number[]>();
  for (let k = 0; k < nombre; k++) {
    const r = trouver(k);
    groupes.set(r, [...(groupes.get(r) ?? []), k]);
  }
  return [...groupes.values()];
}
